<script setup lang="ts">
import { computed, useSlots } from 'vue';

import { AppLoadingDots, AppSpinner } from '../appLoaders/index';
import AppSvgIcon from '../appSvgIcon/AppSvgIcon.vue';
import AppRawButton from '../appRawButton/AppRawButton.vue';

import type {
  AppButtonIconProperties,
  AppButtonSize,
  AppButtonState,
  AppButtonType,
} from './AppButton.model';

interface AppButtonProps {
  type: AppButtonType;
  state: AppButtonState;
  size: AppButtonSize;
  noBorder?: boolean;
  leftIcon?: AppButtonIconProperties;
  rightIcon?: AppButtonIconProperties;
  wide?: boolean;
  spaceBetween?: boolean;
  ariaLabel?: string;
}

const props = defineProps<AppButtonProps>();

interface AppButtonEmits {
  (eventName: 'click', event: MouseEvent): void;
}

const emit = defineEmits<AppButtonEmits>();

const slots = useSlots();
const hasTextSlot = computed(() => !!slots['default']);

const defaultIconSizeForButton = computed(() =>
  props.size === 'small' ? '18' : '24'
);

const leftIconEffectiveSize = computed(
  () => props.leftIcon?.size || defaultIconSizeForButton.value
);

const rightIconEffectiveSize = computed(
  () => props.rightIcon?.size || defaultIconSizeForButton.value
);

function onClick(event: MouseEvent): void {
  if (props.state === 'enabled') {
    emit('click', event);
  }
}
</script>

<template>
  <AppRawButton
    class="app-button"
    :disabled="props.state === 'disabled'"
    :class="[
      [props.type],
      [props.size],
      [props.state],
      props.wide ? 'wide' : undefined,
      props.spaceBetween ? 'space-between' : undefined,
      props.noBorder ? 'no-border' : undefined,
      hasTextSlot ? undefined : 'square',
    ]"
    :aria-label="props.ariaLabel"
    @click="onClick"
  >
    <AppSpinner v-if="props.state === 'loadingSpinner'" />
    <AppLoadingDots v-else-if="props.state === 'loadingDots'" type="white" />
    <template v-else>
      <div class="left-content">
        <AppSvgIcon
          v-if="props.leftIcon"
          :name="props.leftIcon.name"
          :size="leftIconEffectiveSize"
        />
        <div v-if="hasTextSlot" class="text-slot">
          <slot />
        </div>
      </div>
      <AppSvgIcon
        v-if="props.rightIcon"
        :name="props.rightIcon.name"
        :size="rightIconEffectiveSize"
      />
    </template>
  </AppRawButton>
</template>

<style scoped lang="scss">
@use '../../styles/_button-mixins.scss' as *;
@use '../../styles/fonts' as *;

.app-button {
  .left-content {
    display: flex;
    align-items: center;
    .text-slot {
      transform: translateY(1px);
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
  @include button-base;

  &.important {
    @include button-type-important;
  }

  &.primary {
    @include button-type-primary;
  }

  &.secondary {
    @include button-type-secondary;
  }

  &.jungle {
    @include button-type-jungle;
  }

  &.modern {
    @include button-type-modern;

    &.small {
      @include font-label-s-bold;
    }
    &.medium {
      @include font-label-m-bold;
    }
    &.large {
      @include font-label-l-bold;
    }
  }

  &.modern-light {
    @include button-type-modern;

    &.small {
      @include font-label-s-light;
    }
    &.medium {
      @include font-label-m-light;
    }
    &.large {
      @include font-label-l-light;
    }
  }

  &.text {
    @include button-type-text;
  }

  &.small {
    @include app-button-size-small;
  }

  &.medium {
    @include app-button-size-medium;
  }

  &.large {
    @include app-button-size-large;
  }

  &.wide {
    width: 100%;
  }

  &.no-border {
    border: none;
  }

  &.align-left {
    justify-content: flex-start;
  }

  &.align-right {
    justify-content: flex-end;
  }

  &.space-between {
    justify-content: space-between;
  }
}
</style>
